<template>
  <Teleport to="body">
    <Transition name="low-code-popup-fade">
      <div
        v-if="popupState.visible"
        class="low-code-popup-host"
        :data-platform="popupState.platform"
        :style="{ zIndex: popupZIndex }"
        @click.self="handleMaskClick"
      >
        <section
          ref="panelRef"
          class="low-code-popup-host__panel"
          :class="`is-${placement}`"
          :style="panelStyle"
          role="dialog"
          aria-modal="true"
          :aria-label="popupState.title || ct('低代码弹窗页')"
        >
          <header class="low-code-popup-host__header">
            <h2>{{ popupState.title }}</h2>
            <button
              ref="closeButtonRef"
              class="low-code-popup-host__close"
              type="button"
              :aria-label="ct('关闭弹窗')"
              @click="handleClose"
            >
              ×
            </button>
          </header>
          <div class="low-code-popup-host__content">
            <EmbeddedLowCodePageRender
              :key="popupState.requestId"
              :page-id="popupState.pageId"
              :is-mobile="popupState.platform !== 'pc'"
              :parent-context="popupState.parentContext"
              :popup-controller="popupController"
            />
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
const ct = useCommonUiTranslation()

import { useZIndex } from "element-plus";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import EmbeddedLowCodePageRender from "@/components/render/EmbeddedLowCodePageRender.vue";
import { useLowCodeActionRuntime } from "@/composables/useLowCodeActionRuntime.js";
import { popupSizeToCss } from "@/utils/lowCodePopupPage.js";

const { popupState, closeLowCodePopup } = useLowCodeActionRuntime();
const closeButtonRef = ref(null);
const panelRef = ref(null);
const popupZIndex = ref();
const { nextZIndex } = useZIndex();
let previousBodyOverflow = "";
let previousActiveElement = null;
let historyMarkerActive = false;

const placement = computed(
  () => popupState.value.presentation?.placement || "center"
);
const panelStyle = computed(() => {
  const presentation = popupState.value.presentation || {};
  return {
    width: popupSizeToCss(presentation.width, { value: 960, unit: "px" }),
    height: popupSizeToCss(presentation.height, { value: 720, unit: "px" }),
  };
});
const popupController = {
  close: (result) => handleClose(result),
  get platform() {
    return popupState.value.platform;
  },
  get popupId() {
    return popupState.value.requestId;
  },
};

function handleMaskClick() {
  if (popupState.value.presentation?.maskClosable) handleClose();
}

function handleClose(result) {
  if (historyMarkerActive && process.client) {
    historyMarkerActive = false;
    window.history.back();
  }
  closeLowCodePopup(result);
}

function handlePopState() {
  if (!historyMarkerActive || !popupState.value.visible) return;
  historyMarkerActive = false;
  closeLowCodePopup();
}

function handleKeydown(event) {
  if (popupState.value.visible && event.key === "Escape") handleClose();
}

function normalizeNativeMessage(rawData) {
  if (!rawData) return null;
  if (typeof rawData === "object") return rawData;
  if (typeof rawData !== "string") return null;
  try {
    return JSON.parse(rawData);
  } catch (_error) {
    return null;
  }
}

function handleNativeMessage(event) {
  const data = normalizeNativeMessage(event?.data ?? event?.detail);
  if (
    data?.event !== "lowCodePopupClosed" ||
    (data.requestId && data.requestId !== popupState.value.requestId)
  ) {
    return;
  }
  closeLowCodePopup(data.result);
}

watch(
  () => popupState.value.visible,
  async (visible) => {
    if (!process.client) return;
    if (visible) {
      popupZIndex.value = nextZIndex();
      previousActiveElement = document.activeElement;
      previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      if (
        popupState.value.platform !== "pc" &&
        !historyMarkerActive
      ) {
        window.history.pushState(
          { lowCodePopupPage: popupState.value.requestId },
          ""
        );
        historyMarkerActive = true;
      }
      await nextTick();
      closeButtonRef.value?.focus?.();
      return;
    }
    document.body.style.overflow = previousBodyOverflow;
    previousActiveElement?.focus?.();
    previousActiveElement = null;
  }
);

onMounted(() => {
  window.addEventListener("keydown", handleKeydown);
  window.addEventListener("popstate", handlePopState);
  window.addEventListener("message", handleNativeMessage);
  window.addEventListener("lowCodePopupClosed", handleNativeMessage);
  window.__LOW_CODE_POPUP_PAGE__ = {
    close(requestId, result) {
      handleNativeMessage({
        data: { event: "lowCodePopupClosed", requestId, result },
      });
    },
  };
});

onBeforeUnmount(() => {
  if (!process.client) return;
  window.removeEventListener("keydown", handleKeydown);
  window.removeEventListener("popstate", handlePopState);
  window.removeEventListener("message", handleNativeMessage);
  window.removeEventListener("lowCodePopupClosed", handleNativeMessage);
  document.body.style.overflow = previousBodyOverflow;
  if (window.__LOW_CODE_POPUP_PAGE__) delete window.__LOW_CODE_POPUP_PAGE__;
});
</script>

<style scoped>
.low-code-popup-host {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--el-overlay-color-lighter, rgba(0, 0, 0, 0.5));
}

.low-code-popup-host__panel {
  position: absolute;
  display: flex;
  max-width: calc(100vw - 32px);
  max-height: calc(100dvh - 32px);
  overflow: hidden;
  flex-direction: column;
  border-radius: var(--el-border-radius-base, 6px);
  background: var(--el-bg-color, #fff);
  box-shadow: var(--el-box-shadow-dark, 0 16px 48px rgba(0, 0, 0, 0.24));
}

.low-code-popup-host__panel.is-center {
  top: 50%;
  left: 50%;
  max-width: 90vw;
  max-height: 90dvh;
  transform: translate(-50%, -50%);
}

.low-code-popup-host__panel.is-bottom {
  bottom: 0;
  left: 50%;
  max-width: 100vw;
  max-height: calc(100dvh - env(safe-area-inset-top, 0px));
  transform: translateX(-50%);
  border-radius: var(--el-border-radius-base, 6px)
    var(--el-border-radius-base, 6px) 0 0;
}

.low-code-popup-host__panel.is-side {
  top: 50%;
  right: 0;
  max-height: 100dvh;
  transform: translateY(-50%);
  border-radius: var(--el-border-radius-base, 6px) 0 0
    var(--el-border-radius-base, 6px);
}

.low-code-popup-host__header {
  display: flex;
  min-height: 52px;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 16px;
  padding: 0 16px;
  border-bottom: 1px solid var(--el-border-color-lighter, #ebeef5);
}

.low-code-popup-host__header h2 {
  overflow: hidden;
  margin: 0;
  color: var(--el-text-color-primary, #303133);
  font-size: var(--el-font-size-medium, 16px);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.low-code-popup-host__close {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: var(--el-border-radius-small, 4px);
  background: transparent;
  color: var(--el-text-color-secondary, #909399);
  cursor: pointer;
  font-size: 24px;
}

.low-code-popup-host__close:hover,
.low-code-popup-host__close:focus-visible {
  background: var(--el-fill-color-light, #f5f7fa);
  color: var(--el-color-primary, #409eff);
  outline: none;
}

.low-code-popup-host__content {
  min-width: 0;
  min-height: 0;
  overflow: auto;
  flex: 1;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  overscroll-behavior: contain;
}

.low-code-popup-fade-enter-active,
.low-code-popup-fade-leave-active {
  transition: opacity 0.18s ease;
}

.low-code-popup-fade-enter-active .low-code-popup-host__panel,
.low-code-popup-fade-leave-active .low-code-popup-host__panel {
  transition: transform 0.22s ease;
}

.low-code-popup-fade-enter-from,
.low-code-popup-fade-leave-to {
  opacity: 0;
}

.low-code-popup-fade-enter-from .low-code-popup-host__panel.is-center,
.low-code-popup-fade-leave-to .low-code-popup-host__panel.is-center {
  transform: translate(-50%, -48%) scale(0.98);
}

.low-code-popup-fade-enter-from .low-code-popup-host__panel.is-bottom,
.low-code-popup-fade-leave-to .low-code-popup-host__panel.is-bottom {
  transform: translate(-50%, 100%);
}

.low-code-popup-fade-enter-from .low-code-popup-host__panel.is-side,
.low-code-popup-fade-leave-to .low-code-popup-host__panel.is-side {
  transform: translate(100%, -50%);
}

@media (max-width: 640px) {
  .low-code-popup-host {
    padding: 8px;
  }

  .low-code-popup-host__panel {
    max-width: calc(100vw - 16px);
    max-height: calc(100dvh - 16px);
  }

  .low-code-popup-host__panel.is-side {
    max-width: 100vw;
  }

  .low-code-popup-host__panel.is-bottom {
    max-width: 100vw;
  }
}
</style>
